import { jsonKeyMapping } from "../Config/Constants";
import { TemplateObject } from "../Models/TemplateObject";

// Declare global process for React environment
declare const process: {
  env: {
    PUBLIC_URL?: string;
  };
};

export interface QuestionTypeResult {
  icon: string;
  category: "address" | "case" | "person" | "misc";
  confidence: number;
}

// Keywords for different question categories based on real template analysis
const KEYWORDS = {
  address: [
    "address",
    "street",
    "city",
    "state",
    "zip",
    "zipcode",
    "postal",
    "county",
    "location",
    "residence",
    "where",
    "live",
    "located",
    "mailing",
    "billing",
    "home address",
    "business address",
    "contact address",
    "postal code",
    "apartment",
    "suite",
    "unit",
    "building",
    "place of residence",
    "residing",
    "principal place",
    "property address",
    "premises",
    "dwelling",
  ],
  person: [
    // Basic person identifiers
    "name",
    "first name",
    "last name",
    "full name",
    "middle name",
    "party",
    "spouse",
    "husband",
    "wife",
    "person",
    "individual",
    "client",
    "customer",
    "applicant",
    "borrower",
    "lender",
    "tenant",
    "landlord",
    "employee",
    "employer",
    "contractor",
    "vendor",
    "buyer",
    "seller",
    "witness",
    "guardian",
    "parent",
    "child",
    "children",
    "beneficiary",
    "heir",
    "trustee",
    "executor",
    "attorney",
    "agent",
    "representative",
    "contact person",

    // Role-specific identifiers from templates
    "guarantor",
    "co-signer",
    "cosigner",
    "co signer",
    "surety",
    "backer",
    "grantor",
    "grantee",
    "lessor",
    "lessee",
    "releasor",
    "releasee",
    "assignor",
    "assignee",
    "licensor",
    "licensee",
    "mortgagor",
    "mortgagee",
    "promisor",
    "promisee",
    "obligor",
    "obligee",
    "trustor",
    "settlor",

    // Family relationships
    "married",
    "unmarried",
    "single",
    "divorced",
    "widowed",
    "separated",
    "minor",
    "adult",
    "dependent",
    "independent",
    "relative",
    "next of kin",

    // Business roles
    "owner",
    "director",
    "officer",
    "shareholder",
    "member",
    "partner",
    "principal",
    "agent",
    "consultant",
    "advisor",
    "manager",
    "supervisor",
  ],
  case: [
    // Legal proceedings
    "case",
    "lawsuit",
    "litigation",
    "court",
    "legal",
    "dispute",
    "claim",
    "proceeding",
    "action",
    "suit",
    "trial",
    "hearing",
    "judgment",
    "ruling",
    "verdict",
    "settlement",
    "arbitration",
    "mediation",
    "legal matter",
    "legal action",
    "legal proceeding",
    "docket",
    "case number",
    "cause of action",
    "complaint",
    "petition",
    "motion",
    "appeal",
    "jurisdiction",
    "venue",
    "signed",
    // Legal documents and formalities
    "notary",
    "notarization",
    "notarized",
    "acknowledgment",
    "affidavit",
    "oath",
    "sworn",
    "witness signature",
    "witnesses",
    "execution",
    "recording",
    "filing",
    "registration",
    "certification",

    // Legal concepts
    "liability",
    "damages",
    "breach",
    "default",
    "violation",
    "infringement",
    "remedy",
    "relief",
    "injunction",
    "restraining order",
    "enforcement",
    "compliance",
    "waiver",
    "release",
    "discharge",
    "indemnification",
    "hold harmless",
    "covenant",
    "representation",
    "warranty",
  ],
};

// Form type patterns that indicate specific categories
const FORM_TYPE_PATTERNS = {
  address: ["text", "select"], // Address fields are usually text or select (for states)
  person: ["text"], // Person names are typically text fields
  case: ["text", "textarea", "select"], // Case info can be various types
};

/**
 * Analyzes a single form field to determine its question type
 */
export function analyzeFormField(formField: any): QuestionTypeResult {
  const label = (formField[jsonKeyMapping.FORM_LABEL] || "").toLowerCase();
  const placeholder = (
    formField[jsonKeyMapping.FORM_PLACEHOLDER] || ""
  ).toLowerCase();
  const id = (formField[jsonKeyMapping.FORM_ID] || "").toLowerCase();
  const type = formField[jsonKeyMapping.FORM_TYPE] || "";

  // Combine all text for analysis
  const combinedText = `${label} ${placeholder} ${id}`;

  // Strong pattern matching for specific cases
  if (/guarantor|co.?signer|cosigner/.test(combinedText)) {
    if (/name/.test(combinedText)) {
      return {
        icon: getIconPath("person"),
        category: "person",
        confidence: 0.9,
      };
    }
    if (/address|street|city|state|zip/.test(combinedText)) {
      return {
        icon: getIconPath("address"),
        category: "address",
        confidence: 0.9,
      };
    }
  }

  // Policy questions should generally be misc unless they're about legal proceedings
  if (/policy|policies/.test(combinedText)) {
    if (/legal|court|case|proceeding/.test(combinedText)) {
      return {
        icon: getIconPath("case"),
        category: "case",
        confidence: 0.8,
      };
    }
    return {
      icon: getIconPath("misc"),
      category: "misc",
      confidence: 0.7,
    };
  }

  // Calculate scores for each category
  const scores = {
    address: calculateScore(combinedText, KEYWORDS.address),
    person: calculateScore(combinedText, KEYWORDS.person),
    case: calculateScore(combinedText, KEYWORDS.case),
    misc: 0, // Default fallback
  };

  // Apply form type bonuses
  if (FORM_TYPE_PATTERNS.address.includes(type) && scores.address > 0) {
    scores.address += 0.2;
  }
  if (FORM_TYPE_PATTERNS.person.includes(type) && scores.person > 0) {
    scores.person += 0.2;
  }
  if (FORM_TYPE_PATTERNS.case.includes(type) && scores.case > 0) {
    scores.case += 0.2;
  }

  // Find the category with the highest score
  const maxCategory = Object.keys(scores).reduce((a, b) =>
    scores[a as keyof typeof scores] > scores[b as keyof typeof scores] ? a : b
  ) as keyof typeof scores;

  const maxScore = scores[maxCategory];

  // If no strong match found, default to misc
  const category = maxScore > 0.1 ? maxCategory : "misc";
  const confidence = maxScore;

  return {
    icon: getIconPath(category as "address" | "case" | "person" | "misc"),
    category: category as "address" | "case" | "person" | "misc",
    confidence,
  };
}

/**
 * Analyzes all form fields in a step to determine the primary question type
 */
export function analyzeStepQuestionType(
  stepDetails: TemplateObject
): QuestionTypeResult {
  const formGroup = stepDetails[jsonKeyMapping.FORM_GROUP];

  if (!formGroup || !Array.isArray(formGroup) || formGroup.length === 0) {
    // No form fields, analyze step title and description
    return analyzeStepContent(stepDetails);
  }

  // First check step title for strong indicators
  const stepTitleAnalysis = analyzeStepContent(stepDetails);
  if (stepTitleAnalysis.confidence > 0.3) {
    return stepTitleAnalysis;
  }

  // Analyze each form field
  const fieldAnalyses = formGroup.map((field) => analyzeFormField(field));

  // Combine results - find the most common category with highest confidence
  const categoryScores: Record<
    string,
    { count: number; totalConfidence: number; maxConfidence: number }
  > = {};

  fieldAnalyses.forEach((analysis) => {
    if (!categoryScores[analysis.category]) {
      categoryScores[analysis.category] = {
        count: 0,
        totalConfidence: 0,
        maxConfidence: 0,
      };
    }
    categoryScores[analysis.category].count++;
    categoryScores[analysis.category].totalConfidence += analysis.confidence;
    categoryScores[analysis.category].maxConfidence = Math.max(
      categoryScores[analysis.category].maxConfidence,
      analysis.confidence
    );
  });

  // Find the best category based on weighted score
  let bestCategory = "misc";
  let bestScore = 0;

  Object.entries(categoryScores).forEach(([category, data]) => {
    const avgConfidence = data.totalConfidence / data.count;
    // Weight: 40% count dominance, 30% max confidence, 30% average confidence
    const weightedScore =
      (data.count / fieldAnalyses.length) * 0.4 +
      data.maxConfidence * 0.3 +
      avgConfidence * 0.3;

    if (weightedScore > bestScore) {
      bestScore = weightedScore;
      bestCategory = category;
    }
  });

  return {
    icon: getIconPath(bestCategory as "address" | "case" | "person" | "misc"),
    category: bestCategory as "address" | "case" | "person" | "misc",
    confidence: bestScore,
  };
}

/**
 * Analyzes step title and description when there are no form fields
 */
function analyzeStepContent(stepDetails: TemplateObject): QuestionTypeResult {
  const title = (stepDetails[jsonKeyMapping.SLUG] || "").toLowerCase();
  const description = (
    stepDetails[jsonKeyMapping.DESCRIPTION] || ""
  ).toLowerCase();
  const combinedText = `${title} ${description}`;

  // Check for strong title patterns that should override field analysis
  const strongPatterns = {
    address: [
      /address/i,
      /location/i,
      /residence/i,
      /mailing/i,
      /contact.{0,20}information/i,
      /where.{0,20}(live|located|reside)/i,
      /property.{0,20}address/i,
    ],
    person: [
      /party.{0,20}information/i,
      /guarantor/i,
      /co.?signer/i,
      /cosigner/i,
      /(first|second|third).{0,20}party/i,
      /personal.{0,20}information/i,
      /contact.{0,20}person/i,
      /who.{0,20}(is|are)/i,
    ],
    case: [
      /notary/i,
      /signature/i,
      /witness/i,
      /execution/i,
      /acknowledgment/i,
      /legal.{0,20}(proceeding|action|matter)/i,
      /court/i,
      /case/i,
      /lawsuit/i,
      /terms.{0,20}(and|&).{0,10}conditions/i,
      /general.{0,20}(policy|terms)/i,
    ],
  };

  // Check for strong patterns first
  for (const [category, patterns] of Object.entries(strongPatterns)) {
    for (const pattern of patterns) {
      if (pattern.test(combinedText)) {
        return {
          icon: getIconPath(category as "address" | "case" | "person" | "misc"),
          category: category as "address" | "case" | "person" | "misc",
          confidence: 0.8, // High confidence for strong patterns
        };
      }
    }
  }

  // Fallback to keyword matching
  const scores = {
    address: calculateScore(combinedText, KEYWORDS.address),
    person: calculateScore(combinedText, KEYWORDS.person),
    case: calculateScore(combinedText, KEYWORDS.case),
    misc: 0,
  };

  const maxCategory = Object.keys(scores).reduce((a, b) =>
    scores[a as keyof typeof scores] > scores[b as keyof typeof scores] ? a : b
  ) as keyof typeof scores;

  const maxScore = scores[maxCategory];
  const category = maxScore > 0.05 ? maxCategory : "misc"; // Lower threshold for step content

  return {
    icon: getIconPath(category as "address" | "case" | "person" | "misc"),
    category: category as "address" | "case" | "person" | "misc",
    confidence: maxScore,
  };
}

/**
 * Calculate keyword match score for given text
 */
function calculateScore(text: string, keywords: string[]): number {
  let score = 0;
  let matches = 0;

  keywords.forEach((keyword) => {
    if (text.includes(keyword)) {
      matches++;
      // Boost score for exact matches
      if (text === keyword) {
        score += 1.0;
      } else if (
        text.includes(` ${keyword} `) ||
        text.startsWith(keyword) ||
        text.endsWith(keyword)
      ) {
        score += 0.8;
      } else {
        score += 0.5;
      }
    }
  });

  // Normalize score
  return matches > 0 ? score / Math.sqrt(text.length) : 0;
}

/**
 * Get the icon path for a category
 */
function getIconPath(category: "address" | "case" | "person" | "misc"): string {
  const iconMap = {
    address: "/static/images/icon/Address-icon.svg",
    case: "/static/images/icon/case.svg",
    person: "/static/images/icon/person.svg",
    misc: "/static/images/icon/misc.svg",
  };

  return (process.env.PUBLIC_URL || "") + iconMap[category];
}

/**
 * Debug function to test the question type identification
 */
export function debugQuestionType(stepDetails: TemplateObject): void {
  console.group("🔍 Question Type Analysis Debug");
  console.log("Step Title:", stepDetails[jsonKeyMapping.SLUG]);
  console.log("Step Description:", stepDetails[jsonKeyMapping.DESCRIPTION]);

  const result = analyzeStepQuestionType(stepDetails);
  console.log("Result:", result);

  const formGroup = stepDetails[jsonKeyMapping.FORM_GROUP];
  if (formGroup && Array.isArray(formGroup)) {
    console.log("Form Fields Analysis:");
    formGroup.forEach((field, index) => {
      const fieldResult = analyzeFormField(field);
      console.log(`  Field ${index + 1}:`, {
        label: field[jsonKeyMapping.FORM_LABEL],
        type: field[jsonKeyMapping.FORM_TYPE],
        result: fieldResult,
      });
    });
  }

  console.groupEnd();
}
